import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { NavBar } from '../../components/NavBar';
import { SharedFooter } from '../../components/SharedFooter';

const artistNavLinks = [
  { label: 'Drawing events', href: '/events' },
  { label: 'Artists', href: '/artists', active: true },
  { label: 'Models', href: '/models' },
];

const images = {
  hero: '/images/for-artists/photo-blue-veil.webp',
  seeClearly: '/images/for-artists/art-charcoal-yellow.webp',
  referencePack: '/images/for-artists/art-watercolor-olja.webp',
};

export function ForArtistsPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0a0908] pt-[72px] text-[#faf9f7]">
      <NavBar
        variant="dark"
        links={artistNavLinks}
        primaryCta={{
          label: 'Browse sessions',
          onClick: () => navigate('/events'),
        }}
      />

      <main>
        <section className="grid border-b border-white/10 lg:min-h-[78vh] lg:grid-cols-2">
          <div className="flex flex-col justify-center px-7 py-14 sm:px-10 lg:justify-self-end lg:px-16 lg:py-24">
            <div className="max-w-[640px]">
              <div className="mb-5 text-[11px] font-semibold uppercase tracking-[0.08em] text-white/40">
                For artists
              </div>
              <h1 className="font-display text-[clamp(54px,7vw,86px)] font-light leading-[0.98] text-white">
                Draw more often.
              </h1>
              <p className="mt-7 max-w-[52ch] text-[20px] font-light leading-[1.55] text-white/[0.62]">
                Join community and model led drawing sessions with artists and educators from all over the world.
              </p>
              <Link
                to="/events"
                className="mt-10 inline-flex h-[54px] items-center justify-center gap-2 rounded-[5px] bg-[#faf9f7] px-7 text-[15px] font-semibold text-[#0a0908] transition hover:bg-white"
              >
                Browse upcoming sessions
                <ArrowIcon />
              </Link>
            </div>
          </div>

          <ArtPanel
            src={images.hero}
            alt="Life model in blue fabric."
            minHeightClass="min-h-[32vh] lg:min-h-0"
            className="border-t border-white/10 lg:border-l lg:border-t-0"
            imageClassName="object-[center_22%]"
          />
        </section>

        <BenefitSection
          imageSide="left"
          image={images.seeClearly}
          imageAlt="Charcoal figure study with yellow highlights."
          title="See clearly."
        >
          Higher resolution than meeting platforms like Zoom. Gridlines, value matching tools you see.
        </BenefitSection>

        <BenefitSection
          imageSide="right"
          image={images.referencePack}
          imageAlt="Watercolor figure study."
          title="Work beyond the session."
        >
          Reference packs give you access to session recordings and high res photos (released at the model’s discretion).
        </BenefitSection>

        <section className="border-t border-white/10 px-7 py-20 text-center sm:px-10 lg:px-16 lg:py-32">
          <Link
            to="/events"
            className="inline-flex h-[54px] items-center justify-center gap-2 rounded-[5px] bg-[#faf9f7] px-7 text-[15px] font-semibold text-[#0a0908] transition hover:bg-white"
          >
            Browse upcoming sessions
            <ArrowIcon />
          </Link>
        </section>
      </main>

      <SharedFooter variant="dark" />
    </div>
  );
}

function BenefitSection({
  imageSide,
  image,
  imageAlt,
  title,
  children,
}: {
  imageSide: 'left' | 'right';
  image: string;
  imageAlt: string;
  title: string;
  children: ReactNode;
}) {
  const imagePanel = (
    <ArtPanel
      src={image}
      alt={imageAlt}
      className={imageSide === 'left' ? 'lg:border-r' : 'lg:border-l'}
    />
  );

  const textPanel = (
    <div className="flex min-h-[360px] flex-col justify-center px-7 py-14 sm:px-10 lg:min-h-[580px] lg:px-[72px] lg:py-24">
      <div className="max-w-[620px]">
        <h2 className="font-display text-[clamp(42px,5vw,64px)] font-light leading-none text-white">
          {title}
        </h2>
        <p className="mt-6 max-w-[42ch] text-[20px] font-light leading-[1.55] text-white/[0.62]">
          {children}
        </p>
      </div>
    </div>
  );

  return (
    <section className="grid border-b border-white/10 lg:grid-cols-2">
      {imageSide === 'left' ? (
        <>
          {imagePanel}
          {textPanel}
        </>
      ) : (
        <>
          <div className="lg:justify-self-end">{textPanel}</div>
          {imagePanel}
        </>
      )}
    </section>
  );
}

function ArtPanel({
  src,
  alt,
  className = '',
  imageClassName = '',
  minHeightClass = 'min-h-[52vh]',
}: {
  src: string;
  alt: string;
  className?: string;
  imageClassName?: string;
  minHeightClass?: string;
}) {
  return (
    <div className={`relative ${minHeightClass} overflow-hidden border-white/10 bg-[#1a1714] ${className}`.trim()}>
      <img
        src={src}
        alt={alt}
        className={`absolute inset-0 h-full w-full object-cover ${imageClassName}`.trim()}
      />
      <div className="pointer-events-none absolute inset-0 shadow-[inset_0_0_80px_rgba(0,0,0,0.45)]" />
    </div>
  );
}

function ArrowIcon() {
  return (
    <svg
      width="14"
      height="10"
      viewBox="0 0 14 10"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M1 5h12M9 1l4 4-4 4" />
    </svg>
  );
}
